import { computed, ref } from "vue";
import { useLots } from "seedgreen-shared/queries/lot";
import { LotDTO } from "seedgreen-shared/dtos/LotDTO";
import { centerOfMass, convex, bbox, simplify } from "@turf/turf";
import _ from "lodash";
import { hashObject } from "@/utils/hash";
import type {
	Point,
	Polygon,
	MultiPolygon,
	BBox,
	FeatureCollection,
} from "geojson";

/**
 * Ranch Boundaries Composable
 *
 * Purpose: Ranches don't have their own geographic boundaries in our system (yet),
 * but we need to display them on maps. This composable solves that by computing
 * approximate ranch boundaries from the locations of all lots that belong to each ranch.
 *
 * We compute two things for each ranch:
 * 1. Center point: The center of mass of all lot boundaries
 * 2. Hull boundary: A convex hull polygon that encompasses all lots
 */

export interface RanchBoundary {
	ranchId: number;
	ranchName: string;
	lotCount: number;
	/** Center point of all lots in the ranch */
	center: Point;
	/** Convex hull containing all lots */
	hull: Polygon | MultiPolygon | null;
	/** Bounding box for quick operations */
	bbox: BBox;
}

// Module-level cache for singleton behavior across all instances of this composable
// This ensures that if multiple components call useRanchBoundaries(), they share
// the same computed data instead of each doing expensive geometry calculations
const ranchBoundariesCache = ref<Record<number, RanchBoundary>>({});
const lastLotsHash = ref<string>("");

/**
 * Compute ranch boundaries from lots data
 * Only includes ranches that have lots with boundaries
 */
function computeRanchBoundaries(lots: LotDTO[]): Record<number, RanchBoundary> {
	// Group lots by ranch, only including lots with boundaries and ranch IDs
	const lotsByRanch = _.groupBy(
		lots.filter((lot) => lot.ranchId && lot.boundary),
		"ranchId",
	);

	const ranchBoundaries: RanchBoundary[] = [];

	for (const [ranchIdStr, ranchLots] of Object.entries(lotsByRanch)) {
		const ranchId = Number(ranchIdStr);

		try {
			// Create feature collection of all lot boundaries
			const lotFeatures = ranchLots
				.filter(
					(
						lot,
					): lot is LotDTO & {
						boundary: NonNullable<LotDTO["boundary"]>;
					} => lot.boundary !== undefined && lot.boundary !== null,
				)
				.map((lot) => ({
					type: "Feature" as const,
					geometry: lot.boundary,
					properties: {},
				}));

			const featureCollection = {
				type: "FeatureCollection" as const,
				features: lotFeatures,
			} as FeatureCollection;

			// Calculate center of mass of all lots
			const center = centerOfMass(featureCollection);

			// Calculate convex hull containing all lots
			let hull: Polygon | MultiPolygon | null = null;
			try {
				const rawHull = convex(featureCollection);
				if (rawHull) {
					// Simplify to keep the boundaries from getting ridiculous
					hull = simplify(rawHull.geometry, { tolerance: 0.00001 });
				}
			} catch (error) {
				console.warn(
					`Failed to compute hull for ranch ${ranchId}:`,
					error,
				);
			}

			// Calculate bounding box
			const boundingBox = bbox(featureCollection);

			ranchBoundaries.push({
				ranchId,
				ranchName: ranchLots[0].ranchName || `Ranch ${ranchId}`,
				lotCount: ranchLots.length,
				center: center.geometry,
				hull,
				bbox: boundingBox,
			});
		} catch (error) {
			console.warn(
				`Failed to compute boundary for ranch ${ranchId}:`,
				error,
			);
		}
	}

	return _.keyBy(ranchBoundaries, "ranchId");
}

/**
 * Create a hash of lot data that affects ranch boundaries
 *
 * We only care about lots that have both a ranchId and a boundary, since those are
 * the only ones that contribute to ranch boundary calculations. This hash will only
 * change if:
 * - A lot gets assigned to a different ranch (ranchId changes)
 * - A lot gets a boundary added/removed (affects the filter)
 * - The set of lots changes (lot added/removed)
 *
 * Other lot property changes (like names, dates, etc.) won't trigger recalculation.
 */
function createLotsHash(lots: LotDTO[]): string {
	return lots
		.filter((lot) => lot.ranchId && lot.boundary)
		.map((lot) => {
			// Create a compact hash of the boundary to detect geometry changes
			const boundaryHash = hashObject(lot.boundary);
			return `${lot.id}-${lot.ranchId}-${boundaryHash}`;
		})
		.sort()
		.join("|");
}

/**
 * Get ranch boundaries computed from lot data
 * Uses singleton caching to avoid recomputation across multiple calls
 */
export function useRanchBoundaries() {
	const { lots } = useLots();

	// Computed boundaries with smart caching that avoids expensive recalculation
	const ranchBoundariesById = computed<Record<number, RanchBoundary>>(() => {
		const currentLots = lots.value ?? [];
		const currentLotsHash = createLotsHash(currentLots);

		// Only recompute ranch boundaries if the lot data that affects them has changed.
		// This prevents expensive turf.js geometry calculations from running on every
		// reactive update (like when lot names change, etc.)
		if (currentLotsHash !== lastLotsHash.value) {
			lastLotsHash.value = currentLotsHash;
			ranchBoundariesCache.value = computeRanchBoundaries(currentLots);
		}

		return ranchBoundariesCache.value;
	});

	// Convenience computed for getting just the boundary objects
	const ranchBoundaries = computed<RanchBoundary[]>(() =>
		Object.values(ranchBoundariesById.value),
	);

	/**
	 * Get boundary data for a specific ranch
	 */
	function getRanchBoundary(ranchId: number): RanchBoundary | undefined {
		return ranchBoundariesById.value[ranchId];
	}

	/**
	 * Check if a ranch has boundary data available
	 */
	function hasRanchBoundary(ranchId: number): boolean {
		return ranchId in ranchBoundariesById.value;
	}

	return {
		/** Ranch boundaries keyed by ranch ID for O(1) lookup */
		ranchBoundariesById,
		/** Array of all ranch boundaries */
		ranchBoundaries,
		/** Get boundary for a specific ranch */
		getRanchBoundary,
		/** Check if a ranch has boundary data */
		hasRanchBoundary,
	};
}
